import React, { useState, useEffect, useRef, useCallback } from "react";
import QRCode from "qrcode";
import JSZip from "jszip";
import S from "../styles";
import { generateQRCodeSTL } from "../utils/stlGenerator";
import STLPreview from "./STLPreview";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

export default function QRGeneratorPage() {
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  const [size, setSize] = useState(256);
  const [fontSize, setFontSize] = useState(20);
  const [gap, setGap] = useState(10);
  const [textPos, setTextPos] = useState("below"); // for PNG/SVG
  const [stlTextPos, setStlTextPos] = useState("below");
  const [stlBaseHeight, setStlBaseHeight] = useState(2);
  const [stlQrHeight, setStlQrHeight] = useState(3.5);
  const [stlModuleSize, setStlModuleSize] = useState(1);
  const [stlPadding, setStlPadding] = useState(2);
  const [stlTextScale, setStlTextScale] = useState(1);
  const [previewTris, setPreviewTris] = useState(null);

  // Auto-generate preview when STL settings change
  useEffect(() => {
    if (!text.trim()) { setPreviewTris(null); return; }
    const timer = setTimeout(() => {
      generateQRCodeSTL(text.trim(), label.trim() || text.trim(), {
        textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight,
        moduleSize: stlModuleSize, basePadding: stlPadding, textScale: stlTextScale,
      }).then((result) => { if (result) setPreviewTris(result.triangles); });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, label, stlTextPos, stlBaseHeight, stlQrHeight, stlModuleSize, stlPadding, stlTextScale]);
  const [pngUrl, setPngUrl] = useState("");
  const [svgString, setSvgString] = useState("");
  const canvasRef = useRef(null);
  const outputCanvasRef = useRef(null);

  const wrapText = (ctx, labelText, maxW, fs) => {
    ctx.font = `bold ${fs}px 'Helvetica Neue', Arial, sans-serif`;
    const rawLines = labelText ? labelText.split("\n") : [];
    const lines = [];
    rawLines.forEach((raw) => {
      const words = raw.split(" ");
      let cur = "";
      words.forEach((w) => {
        const test = cur ? cur + " " + w : w;
        if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = w; }
        else cur = test;
      });
      if (cur) lines.push(cur);
    });
    return lines;
  };

  const generate = useCallback(() => {
    if (!text.trim()) { setPngUrl(""); setSvgString(""); return; }
    QRCode.toCanvas(canvasRef.current, text.trim(), {
      width: size, margin: 2, color: { dark: "#F4F2EC", light: "#ffffff" },
    }, () => {
      if (!canvasRef.current) return;
      const labelText = label.trim() || text.trim();
      const fs = fontSize;
      const out = outputCanvasRef.current;
      const tempCtx = out.getContext("2d");
      const lines = wrapText(tempCtx, labelText, size - 20, fs);
      const lineH = fs + 6;
      const textBlockH = lines.length > 0 ? Math.abs(gap) + lines.length * lineH + 10 : 0;
      const textBlockW = lines.length > 0 ? Math.max(...lines.map((l) => { tempCtx.font = `bold ${fs}px 'Helvetica Neue', Arial, sans-serif`; return tempCtx.measureText(l).width; })) + 20 : 0;

      let canvasW, canvasH, qrX, qrY, textX, textY, textAlign;
      if (textPos === "below") {
        canvasW = size; canvasH = size + textBlockH; qrX = 0; qrY = 0;
        textX = size / 2; textY = size + gap; textAlign = "center";
      } else if (textPos === "above") {
        canvasW = size; canvasH = size + textBlockH; qrX = 0; qrY = textBlockH;
        textX = size / 2; textY = gap + 10; textAlign = "center";
      } else if (textPos === "left") {
        canvasW = size + textBlockW + Math.abs(gap); canvasH = size; qrX = textBlockW + Math.abs(gap); qrY = 0;
        textX = (textBlockW + Math.abs(gap)) / 2; textY = (size - lines.length * lineH) / 2; textAlign = "center";
      } else { // right
        canvasW = size + textBlockW + Math.abs(gap); canvasH = size; qrX = 0; qrY = 0;
        textX = size + Math.abs(gap) + textBlockW / 2; textY = (size - lines.length * lineH) / 2; textAlign = "center";
      }

      out.width = canvasW; out.height = canvasH;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.drawImage(canvasRef.current, qrX, qrY, size, size);
      if (lines.length > 0) {
        ctx.fillStyle = "#F4F2EC";
        ctx.font = `bold ${fs}px 'Helvetica Neue', Arial, sans-serif`;
        ctx.textAlign = textAlign;
        lines.forEach((line, i) => ctx.fillText(line, textX, textY + (i + 1) * lineH - 2));
      }
      setPngUrl(out.toDataURL("image/png"));
    });
    // SVG
    QRCode.toString(text.trim(), {
      type: "svg", width: size, margin: 2, color: { dark: "#F4F2EC", light: "#ffffff" },
    }, (err, svg) => {
      if (err) return;
      const labelText = label.trim() || text.trim();
      if (labelText) {
        const svgFs = fontSize;
        const svgLineH = svgFs + 6;
        const charW = svgFs * 0.6;
        const maxChars = Math.floor((size - 20) / charW);
        const svgLines = [];
        labelText.split("\n").forEach((raw) => {
          const words = raw.split(" ");
          let cur = "";
          words.forEach((w) => {
            const test = cur ? cur + " " + w : w;
            if (test.length > maxChars && cur) { svgLines.push(cur); cur = w; }
            else cur = test;
          });
          if (cur) svgLines.push(cur);
        });
        const textBlockH = Math.abs(gap) + svgLines.length * svgLineH + 10;
        const textBlockW = Math.max(...svgLines.map((l) => l.length * charW)) + 20;

        let svgW, svgH, qrTransform, textEls;
        if (textPos === "below") {
          svgW = size; svgH = size + textBlockH;
          qrTransform = "";
          textEls = svgLines.map((l, i) => `<text x="${size / 2}" y="${size + gap + (i + 1) * svgLineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${svgFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
        } else if (textPos === "above") {
          svgW = size; svgH = size + textBlockH;
          qrTransform = `transform="translate(0,${textBlockH})"`;
          textEls = svgLines.map((l, i) => `<text x="${size / 2}" y="${gap + 10 + (i + 1) * svgLineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${svgFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
        } else if (textPos === "left") {
          svgW = size + textBlockW + Math.abs(gap); svgH = size;
          qrTransform = `transform="translate(${textBlockW + Math.abs(gap)},0)"`;
          const ty = (size - svgLines.length * svgLineH) / 2;
          textEls = svgLines.map((l, i) => `<text x="${(textBlockW + Math.abs(gap)) / 2}" y="${ty + (i + 1) * svgLineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${svgFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
        } else {
          svgW = size + textBlockW + Math.abs(gap); svgH = size;
          qrTransform = "";
          const ty = (size - svgLines.length * svgLineH) / 2;
          textEls = svgLines.map((l, i) => `<text x="${size + Math.abs(gap) + textBlockW / 2}" y="${ty + (i + 1) * svgLineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${svgFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
        }
        const bgRect = `<rect x="0" y="0" width="${svgW}" height="${svgH}" fill="#ffffff"/>`;
        let modSvg = svg
          .replace(/width="[^"]*"/, `width="${svgW}"`)
          .replace(/height="[^"]*"/, `height="${svgH}"`);
        if (qrTransform) {
          modSvg = modSvg.replace(/<rect/, `${bgRect}<g ${qrTransform}><rect`).replace(/<\/svg>/, `</g>${textEls}</svg>`);
        } else {
          modSvg = modSvg.replace(/<\/svg>/, `${textEls}</svg>`);
        }
        setSvgString(modSvg);
      } else {
        setSvgString(svg);
      }
    });
  }, [text, label, size, fontSize, gap, textPos]);

  useEffect(() => { generate(); }, [generate]);

  const downloadPNG = () => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = `qr-${text.trim().replace(/[^a-zA-Z0-9-_]/g, "_")}.png`;
    a.click();
    markQRGenerated(text.trim());
  };

  const downloadPNGTransparent = () => {
    if (!canvasRef.current || !text.trim()) return;
    const qrCanvas = document.createElement("canvas");
    QRCode.toCanvas(qrCanvas, text.trim(), {
      width: size, margin: 2, color: { dark: "#F4F2EC", light: "#00000000" },
    }, () => {
      const labelText = label.trim() || text.trim();
      const fs = fontSize;
      const tempCtx = document.createElement("canvas").getContext("2d");
      const lines = wrapText(tempCtx, labelText, size - 20, fs);
      const lineH = fs + 6;
      const textBlockH = lines.length > 0 ? Math.abs(gap) + lines.length * lineH + 10 : 0;
      const textBlockW = lines.length > 0 ? Math.max(...lines.map((l) => { tempCtx.font = `bold ${fs}px 'Helvetica Neue', Arial, sans-serif`; return tempCtx.measureText(l).width; })) + 20 : 0;

      let canvasW, canvasH, qrX, qrY, tX, tY;
      if (textPos === "below") { canvasW = size; canvasH = size + textBlockH; qrX = 0; qrY = 0; tX = size / 2; tY = size + gap; }
      else if (textPos === "above") { canvasW = size; canvasH = size + textBlockH; qrX = 0; qrY = textBlockH; tX = size / 2; tY = gap + 10; }
      else if (textPos === "left") { canvasW = size + textBlockW + Math.abs(gap); canvasH = size; qrX = textBlockW + Math.abs(gap); qrY = 0; tX = (textBlockW + Math.abs(gap)) / 2; tY = (size - lines.length * lineH) / 2; }
      else { canvasW = size + textBlockW + Math.abs(gap); canvasH = size; qrX = 0; qrY = 0; tX = size + Math.abs(gap) + textBlockW / 2; tY = (size - lines.length * lineH) / 2; }

      const out = document.createElement("canvas");
      out.width = canvasW; out.height = canvasH;
      const ctx = out.getContext("2d");
      ctx.drawImage(qrCanvas, qrX, qrY, size, size);
      if (lines.length > 0) {
        ctx.fillStyle = "#F4F2EC";
        ctx.font = `bold ${fs}px 'Helvetica Neue', Arial, sans-serif`;
        ctx.textAlign = "center";
        lines.forEach((line, i) => ctx.fillText(line, tX, tY + (i + 1) * lineH - 2));
      }
      const a = document.createElement("a");
      a.href = out.toDataURL("image/png");
      a.download = `qr-${text.trim().replace(/[^a-zA-Z0-9-_]/g, "_")}-transparent.png`;
      a.click();
      markQRGenerated(text.trim());
    });
  };

  const downloadSVG = () => {
    if (!svgString) return;
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${text.trim().replace(/[^a-zA-Z0-9-_]/g, "_")}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    markQRGenerated(text.trim());
  };

  const markQRGenerated = (skus) => {
    ["designshop", "vibratovault"].forEach((store) => {
      fetch(`${API_URL}/api/products/${store}/mark-qr`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ skus: Array.isArray(skus) ? skus : [skus] }),
      }).catch(() => {});
    });
  };

  // Batch generation
  const [batchInput, setBatchInput] = useState("");
  const [batchResults, setBatchResults] = useState([]);
  const [showBatch, setShowBatch] = useState(false);
  const [batchPreview, setBatchPreview] = useState("png"); // "png" or "stl"
  const [batchStlPreviews, setBatchStlPreviews] = useState({});

  // Live-update STL previews when settings change
  useEffect(() => {
    if (batchPreview !== "stl" || batchResults.length === 0) return;
    const timer = setTimeout(() => {
      batchResults.forEach((r) => {
        generateQRCodeSTL(r.sku, r.name, {
          textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight,
          moduleSize: stlModuleSize, basePadding: stlPadding, textScale: stlTextScale,
        }).then((result) => {
          if (result) setBatchStlPreviews((prev) => ({ ...prev, [r.sku]: result.triangles }));
        });
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [batchPreview, batchResults, stlTextPos, stlBaseHeight, stlQrHeight, stlModuleSize, stlPadding, stlTextScale]);

  const generateBatch = () => {
    // Parse entries: SKU | Name on first line, continuation lines below, blank line separates entries
    const rawLines = batchInput.split("\n");
    const entries = [];
    let current = null;
    for (const raw of rawLines) {
      const line = raw.trim();
      if (!line) { current = null; continue; }
      if (line.includes("|")) {
        const parts = line.split("|").map((p) => p.trim());
        current = { sku: parts[0], name: parts[1] || parts[0] };
        entries.push(current);
      } else if (current) {
        current.name += "\n" + line;
      } else {
        current = { sku: line, name: line };
        entries.push(current);
      }
    }
    if (entries.length === 0) return;

    const results = [];
    let done = 0;
    const bSize = size;
    const bFs = fontSize;
    const bGap = gap;
    const bPos = textPos;

    entries.forEach((entry) => {
      const sku = entry.sku;
      const name = entry.name;
      const lineH = bFs + 6;

      const qrCanvas = document.createElement("canvas");
      QRCode.toCanvas(qrCanvas, sku, { width: bSize, margin: 2, color: { dark: "#F4F2EC", light: "#ffffff" } }, () => {
        const out = document.createElement("canvas");
        const tmpCtx = out.getContext("2d");
        const nameLines = wrapText(tmpCtx, name, bSize - 20, bFs);
        const textBlockH = nameLines.length > 0 ? Math.abs(bGap) + nameLines.length * lineH + 10 : 0;
        const textBlockW = nameLines.length > 0 ? Math.max(...nameLines.map((l) => { tmpCtx.font = `bold ${bFs}px 'Helvetica Neue', Arial, sans-serif`; return tmpCtx.measureText(l).width; })) + 20 : 0;

        let canvasW, canvasH, qrX, qrY, tX, tY;
        if (bPos === "below") { canvasW = bSize; canvasH = bSize + textBlockH; qrX = 0; qrY = 0; tX = bSize / 2; tY = bSize + bGap; }
        else if (bPos === "above") { canvasW = bSize; canvasH = bSize + textBlockH; qrX = 0; qrY = textBlockH; tX = bSize / 2; tY = bGap + 10; }
        else if (bPos === "left") { canvasW = bSize + textBlockW + Math.abs(bGap); canvasH = bSize; qrX = textBlockW + Math.abs(bGap); qrY = 0; tX = (textBlockW + Math.abs(bGap)) / 2; tY = (bSize - nameLines.length * lineH) / 2; }
        else { canvasW = bSize + textBlockW + Math.abs(bGap); canvasH = bSize; qrX = 0; qrY = 0; tX = bSize + Math.abs(bGap) + textBlockW / 2; tY = (bSize - nameLines.length * lineH) / 2; }

        out.width = canvasW; out.height = canvasH;
        const ctx = out.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasW, canvasH);
        ctx.drawImage(qrCanvas, qrX, qrY, bSize, bSize);
        if (nameLines.length > 0) {
          ctx.fillStyle = "#F4F2EC";
          ctx.font = `bold ${bFs}px 'Helvetica Neue', Arial, sans-serif`;
          ctx.textAlign = "center";
          nameLines.forEach((ln, i) => ctx.fillText(ln, tX, tY + (i + 1) * lineH - 2));
        }
        const pngData = out.toDataURL("image/png");

        QRCode.toString(sku, { type: "svg", width: bSize, margin: 2, color: { dark: "#F4F2EC", light: "#ffffff" } }, (err, svg) => {
          let svgData = svg || "";
          if (!err && nameLines.length > 0) {
            const charW = bFs * 0.6;
            const maxChars = Math.floor((bSize - 20) / charW);
            const svgLines = [];
            name.split("\n").forEach((raw) => { const words = raw.split(" "); let cur = ""; words.forEach((w) => { const test = cur ? cur + " " + w : w; if (test.length > maxChars && cur) { svgLines.push(cur); cur = w; } else cur = test; }); if (cur) svgLines.push(cur); });
            const svgTextH = Math.abs(bGap) + svgLines.length * lineH + 10;
            const svgTextW = Math.max(...svgLines.map((l) => l.length * charW)) + 20;

            let svgW, svgH, qrTransform, textEls;
            if (bPos === "below") {
              svgW = bSize; svgH = bSize + svgTextH; qrTransform = "";
              textEls = svgLines.map((l, i) => `<text x="${bSize / 2}" y="${bSize + bGap + (i + 1) * lineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${bFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
            } else if (bPos === "above") {
              svgW = bSize; svgH = bSize + svgTextH; qrTransform = `transform="translate(0,${svgTextH})"`;
              textEls = svgLines.map((l, i) => `<text x="${bSize / 2}" y="${bGap + 10 + (i + 1) * lineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${bFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
            } else if (bPos === "left") {
              svgW = bSize + svgTextW + Math.abs(bGap); svgH = bSize; qrTransform = `transform="translate(${svgTextW + Math.abs(bGap)},0)"`;
              const sty = (bSize - svgLines.length * lineH) / 2;
              textEls = svgLines.map((l, i) => `<text x="${(svgTextW + Math.abs(bGap)) / 2}" y="${sty + (i + 1) * lineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${bFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
            } else {
              svgW = bSize + svgTextW + Math.abs(bGap); svgH = bSize; qrTransform = "";
              const sty = (bSize - svgLines.length * lineH) / 2;
              textEls = svgLines.map((l, i) => `<text x="${bSize + Math.abs(bGap) + svgTextW / 2}" y="${sty + (i + 1) * lineH - 2}" text-anchor="middle" font-family="Helvetica Neue, Arial, sans-serif" font-size="${bFs}" font-weight="bold" fill="#F4F2EC">${l}</text>`).join("");
            }
            const bgRect = `<rect x="0" y="0" width="${svgW}" height="${svgH}" fill="#ffffff"/>`;
            svgData = svgData.replace(/width="[^"]*"/, `width="${svgW}"`).replace(/height="[^"]*"/, `height="${svgH}"`);
            if (qrTransform) {
              svgData = svgData.replace(/<rect/, `${bgRect}<g ${qrTransform}><rect`).replace(/<\/svg>/, `</g>${textEls}</svg>`);
            } else {
              svgData = svgData.replace(/<\/svg>/, `${textEls}</svg>`);
            }
          }
          results.push({ sku, name, png: pngData, svg: svgData });
          done++;
          if (done === entries.length) setBatchResults([...results]);
        });
      });
    });
  };

  const downloadFile = (data, filename, type) => {
    const isDataUrl = data.startsWith("data:");
    const blob = isDataUrl ? null : new Blob([data], { type });
    const url = isDataUrl ? data : URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    if (!isDataUrl) URL.revokeObjectURL(url);
  };

  const downloadAllZip = async (type) => {
    const zip = new JSZip();
    batchResults.forEach((r) => {
      const safeName = r.sku.replace(/[^a-zA-Z0-9-_]/g, "_");
      if (type === "png") {
        const base64 = r.png.split(",")[1];
        zip.file(`qr-${safeName}.png`, base64, { base64: true });
      } else {
        zip.file(`qr-${safeName}.svg`, r.svg);
      }
    });
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-codes-${type}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>QR Code Generator</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Generate QR codes for product SKUs</p>
      </div>

      {/* Toggle */}
      <div style={{ marginBottom: 24 }}>
        <nav style={S.nav}>
          <button onClick={() => setShowBatch(false)} style={{ ...S.navBtn, ...(!showBatch ? S.navBtnActive : {}) }}>Single</button>
          <button onClick={() => setShowBatch(true)} style={{ ...S.navBtn, ...(showBatch ? S.navBtnActive : {}) }}>Batch</button>
        </nav>
      </div>

      {!showBatch ? (
        /* ─── Single QR ─── */
        <div>
          <div style={{ maxWidth: 500 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>SKU (encoded in QR)</label>
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Enter SKU..." style={S.input} autoFocus />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Product Name (printed below QR — hit Enter for new line)</label>
              <textarea value={label} onChange={(e) => setLabel(e.target.value)} placeholder={"e.g. Custom Acrylic Sign\nLine 2"} rows={2} style={{ ...S.input, resize: "vertical", fontFamily: "'Archivo', sans-serif", lineHeight: 1.6 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>QR Size (px)</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[128, 256, 512, 1024].map((s) => (
                  <button key={s} onClick={() => setSize(s)} style={{
                    padding: "6px 14px", borderRadius: 0, border: "2px solid #0A0A0F",
                    background: size === s ? "#F4F2EC" : "transparent",
                    color: size === s ? "#0033FF" : "rgba(10,10,15,0.58)",
                    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {s}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Text Size: {fontSize}px</label>
                  <input type="range" min="10" max="48" value={fontSize} onChange={(e) => setFontSize(parseInt(e.target.value))}
                    style={{ width: "100%", accentColor: "#0033FF" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Gap: {gap}px</label>
                  <input type="range" min="-20" max="5" value={gap} onChange={(e) => setGap(parseInt(e.target.value))}
                    style={{ width: "100%", accentColor: "#0033FF" }} />
                </div>
              </div>
              <div>
                <label style={S.label}>Text Position</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {[{ key: "below", label: "Below" }, { key: "above", label: "Above" }, { key: "left", label: "Left" }, { key: "right", label: "Right" }].map((p) => (
                    <button key={p.key} onClick={() => setTextPos(p.key)} style={{
                      flex: 1, padding: "6px 8px", borderRadius: 0, border: `1px solid ${textPos === p.key ? "#0033FF" : "#0A0A0F"}`,
                      background: textPos === p.key ? "#F4F2EC" : "transparent",
                      color: textPos === p.key ? "#0033FF" : "rgba(10,10,15,0.58)",
                      fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                    }}>{p.label}</button>
                  ))}
                </div>
              </div>
            </div>
            {/* Image Preview */}
            {text.trim() && (
              <div style={{ marginTop: 16, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <canvas ref={outputCanvasRef} style={{ borderRadius: 0, maxWidth: "100%" }} />
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{text.trim()}</div>
                <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{size} × {size} px</div>
              </div>
            )}
            {text.trim() && (
              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <label style={S.label}>STL Text Position</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ key: "below", label: "Below" }, { key: "above", label: "Above" }, { key: "left", label: "Left" }, { key: "right", label: "Right" }].map((p) => (
                    <button key={p.key} onClick={() => setStlTextPos(p.key)} style={{
                      flex: 1, padding: "6px 10px", borderRadius: 0, border: "2px solid #0A0A0F",
                      background: stlTextPos === p.key ? "#F4F2EC" : "transparent",
                      color: stlTextPos === p.key ? "#0033FF" : "rgba(10,10,15,0.58)",
                      fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                    }}>{p.label}</button>
                  ))}
                </div>
              </div>
            )}
            {text.trim() && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginTop: 12 }}>
                <div>
                  <label style={S.label}>Base Height: {stlBaseHeight}mm</label>
                  <input type="range" min="0.5" max="5" step="0.5" value={stlBaseHeight} onChange={(e) => setStlBaseHeight(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
                </div>
                <div>
                  <label style={S.label}>QR Height: {stlQrHeight}mm</label>
                  <input type="range" min="1" max="8" step="0.5" value={stlQrHeight} onChange={(e) => setStlQrHeight(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
                </div>
                <div>
                  <label style={S.label}>Block Size: {stlModuleSize}mm</label>
                  <input type="range" min="0.5" max="3" step="0.25" value={stlModuleSize} onChange={(e) => setStlModuleSize(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
                </div>
                <div>
                  <label style={S.label}>Padding: {stlPadding}mm</label>
                  <input type="range" min="0" max="5" step="0.5" value={stlPadding} onChange={(e) => setStlPadding(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
                </div>
                <div>
                  <label style={S.label}>Text Size: {Math.round(stlTextScale * 100)}%</label>
                  <input type="range" min="0.25" max="2" step="0.05" value={stlTextScale} onChange={(e) => setStlTextScale(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#0033FF" }} />
                </div>
              </div>
            )}
            {/* 3D Preview */}
            {text.trim() && (
              <div style={{ marginTop: 16 }}>
                <label style={S.label}>3D Preview</label>
                <STLPreview triangles={previewTris} width={360} height={280} />
              </div>
            )}
            {text.trim() && (
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button onClick={downloadPNG} style={{
                  flex: 1, padding: "12px 14px", borderRadius: 0, border: "none",
                  background: "#0033FF", color: "#FFFFFF",
                  fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  PNG (White BG)
                </button>
                <button onClick={downloadPNGTransparent} style={{
                  flex: 1, padding: "12px 14px", borderRadius: 0, border: "1px solid #0033FF",
                  background: "transparent", color: "#0033FF",
                  fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  PNG (Clear BG)
                </button>
                <button onClick={downloadSVG} style={{
                  flex: 1, padding: "12px 20px", borderRadius: 0, border: "1px solid #0033FF",
                  background: "transparent", color: "#0033FF",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  Download SVG
                </button>
                <button onClick={async () => {
                  const result = await generateQRCodeSTL(text.trim(), label.trim() || text.trim(), { textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight, moduleSize: stlModuleSize, basePadding: stlPadding });
                  if (!result) return;
                  const url = URL.createObjectURL(result.blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `qr-${text.trim().replace(/[^a-zA-Z0-9-_]/g, "_")}.stl`;
                  a.click();
                  URL.revokeObjectURL(url);
                  markQRGenerated(text.trim());
                }} style={{
                  flex: 1, padding: "12px 20px", borderRadius: 0, border: "1px solid #34d399",
                  background: "transparent", color: "#34d399",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  Download STL
                </button>
              </div>
            )}
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>
      ) : (
        /* ─── Batch QR ─── */
        <div>
          {/* Import from Store */}
          <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F", marginBottom: 10 }}>Import from Store</div>
            <div style={{ display: "flex", gap: 10 }}>
              {[{ key: "designshop", label: "Design Shop" }, { key: "vibratovault", label: "Vibrato Vault" }].map((s) => (
                <button key={s.key} onClick={() => {
                  fetch(`${API_URL}/api/products?store=${s.key}`).then((r) => r.json()).then((products) => {
                    if (!Array.isArray(products)) return;
                    const baseNames = new Set();
                    const standaloneProducts = [];
                    products.forEach((p) => {
                      if (p.baseProduct) baseNames.add(p.baseProduct);
                      else if (p.sku) standaloneProducts.push(p);
                    });
                    const lines = [];
                    baseNames.forEach((bp) => lines.push(`BP:${bp} | ${bp}`));
                    standaloneProducts.filter((p) => !p.qrGenerated).forEach((p) => lines.push(`${p.sku} | ${p.name}`));
                    if (lines.length === 0) { alert(`No products to import from ${s.label}.`); return; }
                    setBatchInput((prev) => prev ? prev + "\n\n" + lines.join("\n\n") : lines.join("\n\n"));
                  }).catch(() => {});
                }} style={{
                  flex: 1, padding: "10px 16px", borderRadius: 0, border: "2px solid #0A0A0F",
                  background: "transparent", color: "#0033FF", fontSize: 14, fontWeight: 600,
                  cursor: "pointer", fontFamily: "'Archivo', sans-serif", transition: "all .15s",
                }}>
                  {s.label}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 8, lineHeight: 1.5 }}>
              <span style={{ color: "rgba(10,10,15,0.58)" }}>Individual SKUs:</span> Standalone products import with their SKU as the QR code.
              <br /><span style={{ color: "rgba(10,10,15,0.58)" }}>Base Products:</span> Pack variants are deduped into one QR per physical product (prefixed with BP:). Scan to request inventory for the entire product pool.
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Enter SKUs (one per line)</label>
            <textarea value={batchInput} onChange={(e) => setBatchInput(e.target.value)} rows={8} placeholder={"SKU-001 | Product Name\nSecond Line\n\nSKU-002 | Another Product\n\nSKU-003 | Single Line Name"} style={{ ...S.input, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.8 }} />
            <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 6 }}>Format: SKU | Product Name — hit Enter for a second line. Blank line separates entries.</p>
          </div>

          {/* Customization Controls */}
          <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F", marginBottom: 12 }}>PNG / SVG Settings</div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>QR Size (px)</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[128, 256, 512, 1024].map((s) => (
                  <button key={s} onClick={() => setSize(s)} style={{
                    padding: "6px 14px", borderRadius: 0, border: "2px solid #0A0A0F",
                    background: size === s ? "#F4F2EC" : "transparent",
                    color: size === s ? "#0033FF" : "rgba(10,10,15,0.58)",
                    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                  }}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Text Size: {fontSize}px</label>
                <input type="range" min="10" max="48" value={fontSize} onChange={(e) => setFontSize(parseInt(e.target.value))} style={{ width: "100%", accentColor: "#0033FF" }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Gap: {gap}px</label>
                <input type="range" min="-20" max="5" value={gap} onChange={(e) => setGap(parseInt(e.target.value))} style={{ width: "100%", accentColor: "#0033FF" }} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Text Position</label>
              <div style={{ display: "flex", gap: 4 }}>
                {[{ key: "below", label: "Below" }, { key: "above", label: "Above" }, { key: "left", label: "Left" }, { key: "right", label: "Right" }].map((p) => (
                  <button key={p.key} onClick={() => setTextPos(p.key)} style={{
                    flex: 1, padding: "6px 8px", borderRadius: 0, border: `1px solid ${textPos === p.key ? "#0033FF" : "#0A0A0F"}`,
                    background: textPos === p.key ? "#F4F2EC" : "transparent",
                    color: textPos === p.key ? "#0033FF" : "rgba(10,10,15,0.58)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                  }}>{p.label}</button>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F", marginBottom: 12, marginTop: 16, paddingTop: 12, borderTop: "2px solid #0A0A0F" }}>STL Settings</div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>STL Text Position</label>
              <div style={{ display: "flex", gap: 4 }}>
                {[{ key: "below", label: "Below" }, { key: "above", label: "Above" }, { key: "left", label: "Left" }, { key: "right", label: "Right" }].map((p) => (
                  <button key={p.key} onClick={() => setStlTextPos(p.key)} style={{
                    flex: 1, padding: "6px 8px", borderRadius: 0, border: `1px solid ${stlTextPos === p.key ? "#34d399" : "#0A0A0F"}`,
                    background: stlTextPos === p.key ? "rgba(52,211,153,0.1)" : "transparent",
                    color: stlTextPos === p.key ? "#34d399" : "rgba(10,10,15,0.58)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                  }}>{p.label}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>
              <div>
                <label style={S.label}>Base Height: {stlBaseHeight}mm</label>
                <input type="range" min="0.5" max="5" step="0.5" value={stlBaseHeight} onChange={(e) => setStlBaseHeight(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
              </div>
              <div>
                <label style={S.label}>QR Height: {stlQrHeight}mm</label>
                <input type="range" min="1" max="8" step="0.5" value={stlQrHeight} onChange={(e) => setStlQrHeight(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
              </div>
              <div>
                <label style={S.label}>Block Size: {stlModuleSize}mm</label>
                <input type="range" min="0.5" max="3" step="0.25" value={stlModuleSize} onChange={(e) => setStlModuleSize(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
              </div>
              <div>
                <label style={S.label}>Padding: {stlPadding}mm</label>
                <input type="range" min="0" max="5" step="0.5" value={stlPadding} onChange={(e) => setStlPadding(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#34d399" }} />
              </div>
              <div>
                <label style={S.label}>Text Size: {Math.round(stlTextScale * 100)}%</label>
                <input type="range" min="0.25" max="2" step="0.05" value={stlTextScale} onChange={(e) => setStlTextScale(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#0033FF" }} />
              </div>
            </div>
          </div>

          <button onClick={generateBatch} disabled={!batchInput.trim()} style={{
            padding: "12px 24px", borderRadius: 0, border: "none",
            background: "#0033FF", color: "#FFFFFF",
            fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
            opacity: batchInput.trim() ? 1 : 0.4, marginBottom: 24,
          }}>
            Generate All
          </button>

          {batchResults.length > 0 && (
            <div>
              {/* Preview toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)" }}>Preview</span>
                <div style={{ display: "flex", gap: 4, background: "#FFFFFF", borderRadius: 0, padding: 3, border: "2px solid #0A0A0F" }}>
                  {[{ key: "png", label: "PNG" }, { key: "stl", label: "STL" }].map((opt) => (
                    <button key={opt.key} onClick={() => {
                      setBatchPreview(opt.key);
                      if (opt.key === "stl" && Object.keys(batchStlPreviews).length === 0) {
                        // Generate STL previews
                        batchResults.forEach((r) => {
                          generateQRCodeSTL(r.sku, r.name, {
                            textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight,
                            moduleSize: stlModuleSize, basePadding: stlPadding, textScale: stlTextScale,
                          }).then((result) => {
                            if (result) setBatchStlPreviews((prev) => ({ ...prev, [r.sku]: result.triangles }));
                          });
                        });
                      }
                    }} style={{
                      padding: "5px 14px", borderRadius: 0, border: "none", fontSize: 13, fontWeight: 600,
                      cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      background: batchPreview === opt.key ? "#F4F2EC" : "transparent",
                      color: batchPreview === opt.key ? "#0033FF" : "rgba(10,10,15,0.58)",
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>
              {/* Download All buttons */}
              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <button onClick={() => { downloadAllZip("png"); markQRGenerated(batchResults.map((r) => r.sku)); }} style={{
                  flex: 1, padding: "12px 20px", borderRadius: 0, border: "none",
                  background: "#0033FF", color: "#FFFFFF",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  Download All PNG (.zip)
                </button>
                <button onClick={() => { downloadAllZip("svg"); markQRGenerated(batchResults.map((r) => r.sku)); }} style={{
                  flex: 1, padding: "12px 20px", borderRadius: 0, border: "1px solid #0033FF",
                  background: "transparent", color: "#0033FF",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  Download All SVG (.zip)
                </button>
                <button onClick={() => {
                  const opts = { textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight, moduleSize: stlModuleSize, basePadding: stlPadding, textScale: stlTextScale };
                  Promise.all(batchResults.map((r) => generateQRCodeSTL(r.sku, r.name, opts).then((result) => ({ r, result })))).then(async (all) => {
                    const zip = new JSZip();
                    all.forEach(({ r, result }) => {
                      if (result) {
                        const buf = result.blob.arrayBuffer ? result.blob : result.blob;
                        zip.file(`qr-${r.sku.replace(/[^a-zA-Z0-9-_]/g, "_")}.stl`, buf);
                      }
                    });
                    const zipBlob = await zip.generateAsync({ type: "blob" });
                    const url = URL.createObjectURL(zipBlob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "qr-codes-stl.zip";
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                    markQRGenerated(batchResults.map((r) => r.sku));
                  });
                }} style={{
                  flex: 1, padding: "12px 20px", borderRadius: 0, border: "1px solid #34d399",
                  background: "transparent", color: "#34d399",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                }}>
                  Download All STL (.zip)
                </button>
              </div>
              {/* Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
                {batchResults.map((r, i) => (
                  <div key={i} style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 16, textAlign: "center" }}>
                    {batchPreview === "png" ? (
                      <img src={r.png} alt={r.sku} style={{ width: "100%", borderRadius: 0, marginBottom: 8 }} />
                    ) : (
                      <div style={{ width: "100%", height: 160, marginBottom: 8, borderRadius: 0, overflow: "hidden" }}>
                        {batchStlPreviews[r.sku] ? (
                          <STLPreview triangles={batchStlPreviews[r.sku]} width={180} height={160} />
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(10,10,15,0.58)", fontSize: 13 }}>Loading...</div>
                        )}
                      </div>
                    )}
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: "#0A0A0F", marginBottom: 8 }}>{r.sku}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => { downloadFile(r.png, `qr-${r.sku.replace(/[^a-zA-Z0-9-_]/g, "_")}.png`, "image/png"); markQRGenerated(r.sku); }} style={{
                        flex: 1, padding: "5px 8px", borderRadius: 0, border: "none",
                        background: "#0033FF", color: "#FFFFFF",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      }}>
                        PNG
                      </button>
                      <button onClick={() => { downloadFile(r.svg, `qr-${r.sku.replace(/[^a-zA-Z0-9-_]/g, "_")}.svg`, "image/svg+xml"); markQRGenerated(r.sku); }} style={{
                        flex: 1, padding: "5px 8px", borderRadius: 0, border: "1px solid #0033FF",
                        background: "transparent", color: "#0033FF",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      }}>
                        SVG
                      </button>
                      <button onClick={() => {
                        const dlOpts = { textPosition: stlTextPos, baseHeight: stlBaseHeight, qrHeight: stlQrHeight, moduleSize: stlModuleSize, basePadding: stlPadding, textScale: stlTextScale };
                        generateQRCodeSTL(r.sku, r.name, dlOpts).then((result) => {
                          if (!result) return;
                          // Create a fresh blob copy to avoid browser download caching
                          result.blob.arrayBuffer().then((buf) => {
                            const freshBlob = new Blob([new Uint8Array(buf)], { type: "application/octet-stream" });
                            const url = URL.createObjectURL(freshBlob);
                            const a = document.createElement("a");
                            a.href = url; a.download = `qr-${r.sku.replace(/[^a-zA-Z0-9-_]/g, "_")}.stl`;
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(url), 2000);
                          });
                          markQRGenerated(r.sku);
                        });
                      }} style={{
                        flex: 1, padding: "5px 8px", borderRadius: 0, border: "1px solid #34d399",
                        background: "transparent", color: "#34d399",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      }}>
                        STL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
